import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  CellularBillingReport,
  OverlayBillingRow,
  CellularBillingRow,
  CellularBillingSummary,
  CellularPoolingSnapshot, ReconciliationDraft
} from 'src/app/shared/model/cellular-billing';
import { CellularBillingService } from 'src/app/shared/services/cellular-billing.service';
import { CellularBillingReviewDialogComponent } from './dialogs/cellular-billing-review-dialog.component';
import { CellularBillingThresholdDialogComponent } from './dialogs/cellular-billing-threshold-dialog.component';

type UploadSource = 'license' | 'teal' | 'kore';

@Component({
  selector: 'app-cellular-report-center',
  templateUrl: './cellular-report-center.component.html',
  styleUrls: ['./cellular-report-center.component.scss']
})
export class CellularReportCenterComponent implements OnInit, OnDestroy {
  readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  readonly years: number[];
  readonly periodForm: FormGroup;
  files: Partial<Record<UploadSource, File>> = {};
  rows: CellularBillingRow[] = [];
  pooling: CellularPoolingSnapshot = null;
  poolingRevision = 0;
  reconciliation: ReconciliationDraft = null;
  savedDrafts: Partial<ReconciliationDraft>[] = [];
  overlayRows: OverlayBillingRow[] = [];
  pricingRevision = 0;
  includeOverlay = true;
  duplicatesOnly = false;
  reportTabIndex = 0;
  overlayError = '';
  summary: CellularBillingSummary = null;
  versions: CellularBillingReport[] = [];
  historyError = '';
  periodStatus: 'UNKNOWN' | 'OPEN' | 'COMPLETED' | 'CONFLICT' = 'UNKNOWN';
  finalReportId = '';
  currentReport: CellularBillingReport = null;
  selectedStepIndex = 0;
  previewBusy = false;
  saveBusy = false;
  historyBusy = false;
  reportBusy = false;
  fileError = '';
  reviewReportId = '';
  hasUnsavedChanges = false;
  private hasUploadedDocuments = false;
  private dirtyRevision = 0;
  private readonly subscriptions = new Subscription();
  private historyRequest: Subscription;
  private reportRequest: Subscription;

  constructor(
    formBuilder: FormBuilder,
    private billingService: CellularBillingService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    this.periodForm = formBuilder.group({
      month: [previousMonth.getMonth() + 1, Validators.required],
      year: [previousMonth.getFullYear(), Validators.required]
    });
    this.years = Array.from({ length: today.getFullYear() - 2019 + 1 }, (_, index) => today.getFullYear() + 1 - index);
  }

  ngOnInit(): void {
    this.subscriptions.add(this.route.queryParamMap.subscribe(params => {
      this.reviewReportId = params.get('reviewReportId') || '';
      const draftId = params.get('draftId');
      if (draftId) {
        if (!this.reconciliation || this.reconciliation._id !== draftId) this.resumeDraft(draftId);
      } else if (this.reviewReportId) {
        this.openVersionById(this.reviewReportId);
      } else {
        this.loadVersions();
        this.loadDrafts();
      }
    }));
  }

  ngOnDestroy(): void {
    if (this.historyRequest) this.historyRequest.unsubscribe();
    if (this.reportRequest) this.reportRequest.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  get month(): number { return Number(this.periodForm.value.month); }
  get year(): number { return Number(this.periodForm.value.year); }
  get canCreateReport(): boolean { return this.periodStatus === 'OPEN' && !this.historyBusy && !this.historyError; }
  get canGenerate(): boolean {
    return this.canCreateReport && this.periodForm.valid && !!this.files.license && !!this.files.teal && !!this.files.kore;
  }

  markUnsavedChanges(): void {
    this.poolingRevision += 1;
    if (this.hasUploadedDocuments) {
      this.hasUnsavedChanges = true;
      this.dirtyRevision += 1;
    }
  }

  confirmDiscardChanges(): boolean {
    return !this.hasUnsavedChanges || window.confirm('You have uploaded documents and unsaved report changes. Leave and discard them?');
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges) return;
    event.preventDefault();
    event.returnValue = '';
  }

  onPeriodChanged(): void {
    this.markUnsavedChanges();
    this.rows = [];
    this.pooling = null;
    this.reconciliation = null;
    this.overlayRows = [];
    this.overlayError = '';
    this.includeOverlay = true;
    this.summary = null;
    this.currentReport = null;
    this.loadVersions();
    this.loadDrafts();
  }

  onFileSelected(source: UploadSource, event: Event): void {
    if (!this.canCreateReport) return;
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    const extension = file.name.toLowerCase().split('.').pop();
    const validType = source === 'license'
      ? extension === 'csv' || extension === 'xlsx'
      : extension === 'csv';
    if (!validType || file.size > 20 * 1024 * 1024) {
      this.fileError = validType
        ? `${file.name} exceeds the 20 MB limit.`
        : `${source === 'license' ? 'License must be CSV or XLSX' : source.toUpperCase() + ' must be CSV'}.`;
      input.value = '';
      return;
    }
    this.files = { ...this.files, [source]: file };
    this.reconciliation = null;
    this.currentReport = null; this.rows = []; this.pooling = null; this.overlayRows = []; this.summary = null;
    this.hasUploadedDocuments = true;
    this.markUnsavedChanges();
    this.fileError = '';
  }

  clearFile(source: UploadSource): void {
    const files = { ...this.files };
    delete files[source];
    this.files = files;
    this.reconciliation = null;
    this.currentReport = null; this.rows = []; this.pooling = null; this.overlayRows = []; this.summary = null;
    this.markUnsavedChanges();
    if (!Object.keys(files).length && !this.rows.length && !this.overlayRows.length) {
      this.hasUnsavedChanges = false;
      this.hasUploadedDocuments = false;
    }
  }

  generatePreview(): void {
    if (!this.canGenerate || this.previewBusy) return;
    this.previewBusy = true;
    this.billingService.createPreview(
      this.month,
      this.year,
      this.files.license,
      this.files.teal,
      this.files.kore
    ).pipe(finalize(() => this.previewBusy = false)).subscribe(
      preview => {
        this.periodForm.patchValue({ month: preview.month, year: preview.year }, { emitEvent: false });
        this.acceptPooling(preview);
        this.overlayRows = preview.overlayRows || [];
        this.overlayError = preview.overlayError || '';
        this.includeOverlay = this.overlayRows.length > 0;
        this.summary = preview.summary;
        this.reconciliation = preview.reconciliation ? { ...preview.reconciliation, report: preview } : null;
        this.currentReport = null;
        this.markUnsavedChanges();
        this.selectedStepIndex = 2;
        if (this.reconciliation && this.router) this.router.navigate([], { relativeTo: this.route, queryParams: { draftId: this.reconciliation._id, reviewReportId: null }, queryParamsHandling: 'merge', replaceUrl: true });
        this.toastr.success(this.reconciliation && this.reconciliation.summary.unresolved ? `${this.reconciliation.summary.unresolved} duplicate MAC conflicts need review.` : 'Source validation completed. Continue through reconciliation.');
      },
      error => this.toastr.error(this.errorMessage(error, 'Unable to process the uploaded reports.'))
    );
  }

  saveAndExport(): void {
    if (this.reconciliation && this.reconciliation.status !== 'COMPLETE') { this.selectedStepIndex = 2; this.toastr.error('Complete duplicate MAC reconciliation before saving the billing report.'); return; }
    if (!this.canCreateReport) { this.toastr.error('This month is locked or its billing status could not be verified.'); return; }
    if (this.includeOverlay && this.overlayRows.some(row => row.bundleMapping === 'AMBIGUOUS')) {
      this.toastr.error('Resolve multiple active bundle assignments before saving the Overlay report.');
      return;
    }
    if (this.includeOverlay && this.overlayRows.filter(row => row.dataUsageGb > 14).some(row => row.bundleGb === null || !Number.isFinite(Number(row.bundleGb)) || Number(row.bundleGb) < 0)) {
      this.toastr.error('Enter Bundle GB for every Overlay row before saving. You can apply an allowance in bulk.');
      return;
    }
    const dialogRef = this.dialog.open(CellularBillingThresholdDialogComponent, {
      width: '520px',
      disableClose: true,
      data: { defaultThreshold: 1, usages: this.rows.map(row => row.verificationGb), overlayUsages: this.includeOverlay ? this.overlayRows.map(row => row.dataUsageGb) : undefined }
    });
    this.subscriptions.add(dialogRef.afterClosed().subscribe(threshold => {
      if (threshold === undefined || threshold === null) return;
      this.persistReport(threshold.thresholdGb, threshold.overlayThresholdGb);
    }));
  }

  openVersion(report: CellularBillingReport): void {
    this.openVersionById(report._id);
  }

  showDuplicateVersion(report: CellularBillingReport): void {
    this.duplicatesOnly = true;
    this.reportTabIndex = 0;
    this.openVersion(report);
  }

  refreshOverlayMapping(): void {
    if (!this.canCreateReport || !this.overlayRows.length) return;
    this.subscriptions.add(this.billingService.mapOverlay(this.overlayRows, this.month, this.year).subscribe(rows => {
      this.overlayRows = rows;
      this.markUnsavedChanges();
      this.toastr.success('Overlay bundle mapping refreshed. Save a new version to retain these changes.');
    }, error => this.toastr.error(this.errorMessage(error, 'Unable to refresh bundle mappings.'))));
  }
  onPricingChanged(): void {
    this.pricingRevision += 1;
    this.markUnsavedChanges();
    this.toastr.success('Partner pricing updated in the current draft. Uploaded rows and manual edits are preserved. Save a new version to retain the pricing snapshot.');
  }

  exportVersion(report: CellularBillingReport, event?: Event): void {
    if (event) event.stopPropagation();
    this.downloadReport(report, false);
  }

  fileSize(file: File): string {
    return file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '';
  }

  private persistReport(thresholdGb: number, overlayThresholdGb = 14): void {
    if (!this.canCreateReport) { this.toastr.error('This month is locked or its billing status could not be verified.'); return; }
    const submittedRevision = this.dirtyRevision;
    this.saveBusy = true;
    this.billingService.saveReport(this.month, this.year, thresholdGb, this.rows, this.includeOverlay ? this.overlayRows : undefined, overlayThresholdGb, this.pooling, this.reconciliation)
      .pipe(finalize(() => this.saveBusy = false))
      .subscribe(
        report => {
          this.hasUnsavedChanges = this.dirtyRevision !== submittedRevision;
          this.currentReport = report;
          this.acceptPooling(report);
          this.overlayRows = (report.overlayRows || []).filter(row => row.dataUsageGb > 14);
          this.overlayError = '';
          this.loadVersions();
          this.downloadReport(report, true);
        },
        error => this.toastr.error(this.errorMessage(error, 'Unable to save this report version.'))
      );
  }

  private openVersionById(reportId: string): void {
    if (this.hasUnsavedChanges && !this.confirmDiscardChanges()) return;
    if (this.reportRequest) this.reportRequest.unsubscribe();
    this.reportBusy = true;
    this.reportRequest = this.billingService.getReport(reportId)
      .pipe(finalize(() => this.reportBusy = false))
      .subscribe(
        report => {
          this.hasUnsavedChanges = false;
          this.files = {};
          this.hasUploadedDocuments = false;
          this.reconciliation = null;
          this.periodForm.patchValue({ month: report.month, year: report.year }, { emitEvent: false });
          this.currentReport = report;
          this.acceptPooling(report);
          this.overlayRows = (report.overlayRows || []).filter(row => row.dataUsageGb > 14);
          this.overlayError = '';
          this.includeOverlay = !!report.overlayRows;
          this.summary = {
            licenseRows: report.rowCount,
            tealRows: 0,
            koreRows: 0,
            duplicateMacRows: report.duplicateMacRows,
            invalidMacRows: this.rows.filter(row => row.hasInvalidMac).length
          };
          this.selectedStepIndex = 3;
          this.loadVersions();
        },
        error => this.toastr.error(this.errorMessage(error, 'Unable to load the saved report.'))
      );
  }

  private loadVersions(): void {
    if (!this.periodForm.valid) return;
    if (this.historyRequest) this.historyRequest.unsubscribe();
    this.historyBusy = true;
    this.periodStatus = 'UNKNOWN'; this.finalReportId = '';
    this.historyError = '';
    this.historyRequest = this.billingService.getReportPeriod(this.month, this.year)
      .pipe(finalize(() => this.historyBusy = false))
      .subscribe(
        result => { this.versions = result.reports; this.periodStatus = result.period ? result.period.status : 'UNKNOWN'; this.finalReportId = result.period && result.period.finalReportId || ''; },
        () => {
          this.versions = [];
          this.historyError = 'Unable to load saved reports. Please retry.';
        }
      );
  }

  refreshVersions(): void { this.loadVersions(); }

  reviewStatusLabel(report: CellularBillingReport): string {
    if (report.workflowStage) return { REVIEW: 'Pat review', FINAL_BILLING: 'Final billing', COMPLETED: 'Billing completed', REVIEW_REQUIRED: 'Revision required', SUPERSEDED: 'Superseded · read only', MONTH_CONFLICT: 'Completion conflict' }[report.workflowStage] || 'Ready for review';
    const labels = { READY_FOR_REVIEW: 'Not sent for review', AWAITING_VERIFICATION: 'Awaiting verification',
      APPROVED: 'Approved', REVIEW_REQUIRED: 'Revision required' };
    return labels[report.reviewStatus] || 'Status unavailable';
  }

  private downloadReport(report: CellularBillingReport, askForReview: boolean): void {
    (report.workflowStage ? this.billingService.exportWorkflow(report._id) : this.billingService.exportReport(report._id)).subscribe(
      blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cellular-billing-${report.year}-${String(report.month).padStart(2, '0')}-v${report.version}${report.workflowRevision ? '-r' + report.workflowRevision : ''}.xlsx`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
        if (askForReview) this.askForReview(report);
      },
      error => this.toastr.error(this.errorMessage(error, 'The report was saved, but export failed.'))
    );
  }

  private askForReview(report: CellularBillingReport): void {
    if (this.periodStatus === 'COMPLETED' || this.periodStatus === 'CONFLICT' || report.monthStatus === 'COMPLETED' || report.monthStatus === 'CONFLICT') return;
    const dialogRef = this.dialog.open(CellularBillingReviewDialogComponent, {
      width: '520px',
      data: { version: report.version, recipient: 'verma@rabbit.run' }
    });
    this.subscriptions.add(dialogRef.afterClosed().subscribe(sendEmail => {
      if (!sendEmail) return;
      this.billingService.requestReview(report._id, window.location.origin).subscribe(
        () => {
          this.toastr.success('Review email sent to verma@rabbit.run.');
          this.loadVersions();
        },
        error => this.toastr.error(this.errorMessage(error, 'Unable to send the review email.'))
      );
    }));
  }

  private errorMessage(error: any, fallback: string): string {
    return error && error.error && error.error.error && error.error.error.message
      ? error.error.error.message : fallback;
  }

  acceptPooling(report: { rows?: CellularBillingRow[], pooling?: CellularPoolingSnapshot }): void {
    this.pooling = report.pooling && [2, 3].includes(report.pooling.version) ? report.pooling : null;
    this.rows = this.pooling ? this.pooling.roster : report.rows || [];
  }
  acceptReconciliation(draft: ReconciliationDraft): void {
    this.reconciliation = draft;
    if (!draft.report) return;
    this.acceptPooling(draft.report);
    this.overlayRows = draft.report.overlayRows || [];
    this.overlayError = draft.report.overlayError || '';
    this.includeOverlay = this.overlayRows.length > 0;
    this.summary = draft.report.summary;
  }
  resumeDraft(id: string): void {
    if (this.hasUnsavedChanges && !this.confirmDiscardChanges()) return;
    this.reportBusy = true;
    this.subscriptions.add(this.billingService.getReconciliation(id).pipe(finalize(() => this.reportBusy = false)).subscribe(draft => {
      this.periodForm.patchValue({ month: draft.month, year: draft.year }, { emitEvent: false });
      this.currentReport = null; this.files = {}; this.hasUploadedDocuments = true; this.hasUnsavedChanges = false;
      this.acceptReconciliation(draft); this.selectedStepIndex = 2;
      this.loadVersions();
      if (this.router) this.router.navigate([], { relativeTo: this.route, queryParams: { draftId: id, reviewReportId: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }, error => this.toastr.error(this.errorMessage(error, 'Unable to resume the reconciliation draft.'))));
  }
  private loadDrafts(): void {
    if (!this.billingService.listReconciliations) return;
    const month = this.month, year = this.year;
    this.savedDrafts = [];
    this.subscriptions.add(this.billingService.listReconciliations(month, year).subscribe(drafts => {
      if (this.month === month && this.year === year) this.savedDrafts = drafts;
    }, () => {}));
  }
}
